import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import theme from "../../themes";
import { pageShellCss, pageTitleCss, tableScrollCss } from "../../styles/responsive";
import { Input, Table, Card, Empty, Select, Tooltip } from "antd";
import { useAppDispatch, useAppSelector } from "../../store";
import { getMailConversationsThunk } from "../../store/actions/mail.action";
import { IMailConversation } from "../../models/mail.model";

const { Option } = Select;

const formatMessageTime = (isoString: string): string => {
  const date = new Date(isoString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const messageDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  if (messageDate.getTime() === today.getTime()) {
    return `Today ${timeStr}`;
  } else if (messageDate.getTime() === yesterday.getTime()) {
    return `Yesterday ${timeStr}`;
  } else {
    // Check if within the last 7 days
    const diffDays = Math.floor(
      (today.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (diffDays < 7) {
      const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
      return `${dayName} ${timeStr}`;
    } else {
      // Show full date for older messages
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
  }
};

const CONVERSATION_STATUS = {
  ALL: "all",
  OPEN: "open",
  NEED_ASSIGN: "need_assign",
  ESCALATED: "need_assign",
  FAILED: "failed",
  COMPLETED: "completed",
};

const statusColors: Record<string, { text: string; background: string }> = {
  open: {
    text: theme.colors.blue1,
    background: theme.colors.blue2,
  },
  Open: {
    text: theme.colors.blue1,
    background: theme.colors.blue2,
  },
  need_assign: {
    text: "#E65100",
    background: "#FFF3E0",
  },
  escalated: {
    text: "#E65100",
    background: "#FFF3E0",
  },
  Assigned: {
    text: "#E65100",
    background: "#FFF3E0",
  },
  failed: {
    text: theme.colors.red1,
    background: "#FFEBEE",
  },
  Failed: {
    text: theme.colors.red1,
    background: "#FFEBEE",
  },
  completed: {
    text: theme.colors.green1,
    background: theme.colors.green2,
  },
  Closed: {
    text: theme.colors.green1,
    background: theme.colors.green2,
  },
  Resolved: {
    text: theme.colors.green1,
    background: theme.colors.green2,
  },
};

const Mail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [status, setStatus] = useState<string>(CONVERSATION_STATUS.ALL);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { conversations, loading } = useAppSelector((state) => state.mail);

  const fetchConversations = async (page = currentPage) => {
    await dispatch(
      getMailConversationsThunk({
        page,
        limit: pageSize,
        keyword: debouncedSearchTerm,
        status: status === CONVERSATION_STATUS.ALL ? "" : status,
      })
    ).unwrap();
  };

  // Debounce search input to reduce API calls while typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchInput.trim());
      setCurrentPage(1);
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    fetchConversations(currentPage);
  }, [currentPage, debouncedSearchTerm, status]);

  const handleSearch = (value: string) => {
    setSearchInput(value);
  };

  const getStatusLabel = (value: string) => {
    switch (String(value || "").toLowerCase()) {
      case CONVERSATION_STATUS.OPEN:
        return t("inbound.mail.statusOpen");
      case CONVERSATION_STATUS.NEED_ASSIGN:
      case "escalated":
      case "need_reply":
      case "assigned":
        return t("inbound.whatsapp.needsAssign");
      case CONVERSATION_STATUS.FAILED:
        return t("inbound.mail.statusFailed");
      case CONVERSATION_STATUS.COMPLETED:
      case "closed":
      case "resolved":
        return t("inbound.whatsapp.statusCompleted");
      default:
        return value;
    }
  };

  const columns = [
    {
      title: t("inbound.mail.customerEmail"),
      dataIndex: "user_email",
      key: "user_email",
      width: "15%",
      render: (text: string) => (
        <EmailLink>{text || t("common.notAvailable")}</EmailLink>
      ),
    },
    {
      title: t("inbound.mail.customerName"),
      dataIndex: "recipient_name",
      key: "recipient_name",
      width: "20%",
    },
    {
      title: t("inbound.mail.subject"),
      dataIndex: "messages",
      key: "subject",
      width: "30%",
      render: (messages: IMailConversation["messages"], record: unknown) => {
        const conversation = record as IMailConversation;
        let fullText = "";
        let displayText = "";

        if (conversation.summary && conversation.summary.trim()) {
          fullText = conversation.summary.trim();
          displayText =
            fullText.length > 50 ? `${fullText.substring(0, 50)}...` : fullText;
        } else {
          const lastUserMessage = [...messages].find(
            (msg) => msg.role === "user"
          );
          fullText = lastUserMessage?.content || "";
          displayText =
            fullText.length > 50 ? `${fullText.substring(0, 50)}...` : fullText;
        }

        if (fullText.length > 50) {
          return (
            <Tooltip title={fullText} placement="topLeft">
              <span>{displayText}</span>
            </Tooltip>
          );
        }

        return <span>{displayText}</span>;
      },
    },
    {
      title: t("inbound.mail.status"),
      dataIndex: "status",
      key: "status",
      width: "15%",
      render: (status: string) => (
        <StatusBadge
          colors={
            statusColors[status] || { text: "#374151", background: "#f3f4f6" }
          }
        >
          {getStatusLabel(status)}
        </StatusBadge>
      ),
    },
    {
      title: t("inbound.mail.lastMessage"),
      dataIndex: "last_user_message_at",
      key: "last_user_message_at",
      width: "20%",
      render: (time: string) => (time ? formatMessageTime(time) : "-"),
    },
  ];

  return (
    <FlexRow>
      <MainContent>
        <Title>{t("inbound.mailTitle")}</Title>

        <CardWrapper>
          <Toolbar>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <StatusSelect
                value={status}
                style={{ width: 160, maxWidth: "100%" }}
                size="large"
                onChange={(value) => {
                  setStatus(value as string);
                  setCurrentPage(1);
                }}
              >
                <Option value={CONVERSATION_STATUS.ALL}>
                  {t("inbound.mail.filterByStatus")}
                </Option>
                <Option value={CONVERSATION_STATUS.OPEN}>
                  {t("inbound.mail.statusOpen")}
                </Option>
                <Option value={CONVERSATION_STATUS.NEED_ASSIGN}>
                  {t("inbound.whatsapp.needsAssign")}
                </Option>
                <Option value={CONVERSATION_STATUS.FAILED}>
                  {t("inbound.mail.statusFailed")}
                </Option>
                <Option value={CONVERSATION_STATUS.COMPLETED}>
                  {t("inbound.whatsapp.statusCompleted")}
                </Option>
              </StatusSelect>
            </div>
            <SearchWrapper>
              <SearchIcon />
              <CustomSearch
                placeholder={t("inbound.mail.searchConversations")}
                value={searchInput}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </SearchWrapper>
          </Toolbar>
          <StyledTable
            dataSource={conversations.messages
              .map((conv) => ({
                key: conv._id,
                ...conv,
              }))
              .reverse()}
            columns={columns}
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: conversations.pagination.totalItems,
              position: ["bottomCenter"],
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: false,
            }}
            onRow={(record) => ({
              onClick: () =>
                navigate(`/inbound/mail/${(record as IMailConversation)._id}`),
              style: { cursor: "pointer" },
            })}
            rowKey="key"
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <EmptyStateTitle>
                      {t("inbound.mail.noConversations")}
                    </EmptyStateTitle>
                  }
                />
              ),
            }}
          />
        </CardWrapper>
      </MainContent>
    </FlexRow>
  );
};

// Styled Components
const FlexRow = styled.div`
  display: flex;
  min-height: 100%;
  background-color: ${theme.colors.grey3};
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
  gap: 32px;
  display: flex;
  flex-direction: column;
  ${pageShellCss}
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  ${pageTitleCss}
`;

const CardWrapper = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
  ${tableScrollCss}
`;

const Toolbar = styled.div`
  margin-bottom: 16px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const SearchWrapper = styled.div`
  width: 240px;
  max-width: 100%;
  display: flex;
  background-color: ${theme.colors.grey4};
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  padding: 0px 8px;
  align-items: center;
  box-sizing: border-box;
  gap: 8px;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const CustomSearch = styled(Input)`
  border-radius: 12px;
  height: 40px;
  font-size: 16px;
  border: none;
  box-shadow: none;
  background-color: ${theme.colors.grey4};
  padding: 0;

  &:hover,
  &:focus {
    border: none;
    box-shadow: none;
    background-color: ${theme.colors.grey4};
  }
`;

const EmailLink = styled.span`
  color: ${theme.colors.blue1};
  font-weight: 500;
`;

const StatusBadge = styled.span<{
  colors: { text: string; background: string };
}>`
  background: ${({ colors }) => colors.background};
  color: ${({ colors }) => colors.text};
  border-radius: 12px;
  padding: 5px 12px;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
`;

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const StatusSelect = styled(Select)`
  .ant-select-selector {
    background: ${theme.colors.grey4} !important;
    border: 1px solid ${theme.colors.grey2} !important;
    box-shadow: none !important;
    outline: none !important;
  }
  .ant-select-selector:focus,
  .ant-select-selector:active {
    box-shadow: none !important;
    outline: none !important;
  }
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: white;
    border-bottom: none;
    padding: 10px 16px;
    font-size: 14px;
    line-height: 20px;
    color: ${theme.colors.grey1};
    font-weight: 500;
  }

  .ant-table-thead > tr > th::before {
    display: none;
  }

  .ant-table-tbody > tr > td {
    padding: 16px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    border-bottom: none;
    height: 64px;
  }

  .ant-table-tbody > tr:nth-child(odd) {
    background-color: ${theme.colors.grey3};
  }

  .ant-table-tbody > tr:nth-child(even) {
    background-color: ${theme.colors.grey4};
  }

  .ant-table table {
    border-spacing: 0 4px;
    border-collapse: separate;
  }

  .ant-table-tbody > tr > td:last-child {
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }

  .ant-table-tbody > tr:hover > td {
    background: ${theme.colors.green2} !important;
  }
`;

export default Mail;
